import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Divider,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    Switch,
    FormControlLabel,
    Button,
    Tooltip,
    LinearProgress,
    Snackbar,
    Alert,
    ThemeProvider,
} from "@mui/material";
import axiosService from "../utils/axios";
import type { IUserSettingsVisibility } from "../types/users";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { IProfileSettingsData } from "../types/users";
import { useSelector } from "react-redux";
import type { RootState } from "../store";
import Theme from "../utils/theme";
// --- Types ---------------------------------------------------------------

const visOptions: IUserSettingsVisibility[] = ["personal", "private", "public"];

const Settings = () => {
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = Theme(themeState.mode);

    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [successOpen, setSuccessOpen] = useState(false);

    const [data, setData] = useState<IProfileSettingsData>({
        defaultRecipeVisibility: "private",
        defaultPostVisibility: "private",
        followerOnRequest: false,
        darkMode: false,
    });

    const originalRef = useRef<IProfileSettingsData | null>(null);

    // --- Load on mount -----------------------------------------------------
    useEffect(() => {
        const ac = new AbortController();

        (async () => {
            axiosService
                .get(`${process.env.REACT_APP_API_URL}/users/settings`)
                .then((res) => {
                    setData(res.data.body);
                    originalRef.current = res.data.body;
                })
                .catch((error) => {
                    if (error.name === "AbortError") return;
                    setErrorMsg("Error while retrieving settings. Please try again.");
                })
                .finally(() => {
                    setLoading(false);
                });
        })();

        return () => ac.abort();
    }, []);

    // Derived: dirty check
    const isDirty = useMemo(() => {
        const original = originalRef.current;
        if (!original) return false; // not dirty until we have a baseline
        return JSON.stringify(original) !== JSON.stringify(data);
    }, [data]);

    // Handlers --------------------------------------------------------------
    const handleChange = useCallback(<K extends keyof IProfileSettingsData>(key: K, value: IProfileSettingsData[K]) => {
        setData((prev) => ({ ...prev, [key]: value }));
    }, []);

    const handleReset = useCallback(() => {
        if (originalRef.current) setData(originalRef.current);
    }, []);

    const handleSave = useCallback(() => {
        setSaving(true);
        axiosService
            .post(`${process.env.REACT_APP_API_URL}/users/settings/save`, { userSettings: data })
            .then((res) => {
                setData(res.data.body);
                originalRef.current = res.data.body;
                setSuccessOpen(true);
            })
            .catch((error) => {
                setErrorMsg("Failed to save Settings");
            })
            .finally(() => setSaving(false));
    }, [data]);
    const disabledAll = loading || saving;
    const renderVisOptions = useMemo(
        () =>
            visOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>
                    {opt}
                </MenuItem>
            )),
        []
    );

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <Card sx={{ maxWidth: 720, width: "100%" }}>
                    <CardHeader title="Profile Settings" subheader="Control your social interaction settings." />
                    {(loading || saving) && <LinearProgress />}
                    <Divider />
                    <CardContent>
                        <Stack spacing={3}>
                            {/* Default Recipe Visibility */}
                            <FormControl fullWidth disabled={disabledAll}>
                                <InputLabel id="recipe-vis-label">Default recipe visibility</InputLabel>
                                <Select
                                    labelId="recipe-vis-label"
                                    id="recipe-vis"
                                    label="Default recipe visibility"
                                    value={data.defaultRecipeVisibility}
                                    onChange={(e) => handleChange("defaultRecipeVisibility", e.target.value as IUserSettingsVisibility)}
                                >
                                    {renderVisOptions}
                                </Select>
                                <FormHelperText>Personal: only you | Private: followers you approve | Public: everyone.</FormHelperText>
                            </FormControl>

                            {/* Default Post Visibility */}
                            <FormControl fullWidth disabled={disabledAll}>
                                <InputLabel id="post-vis-label">Default post visibility</InputLabel>
                                <Select
                                    labelId="post-vis-label"
                                    id="post-vis"
                                    label="Default post visibility"
                                    value={data.defaultPostVisibility}
                                    onChange={(e) => handleChange("defaultPostVisibility", e.target.value as IUserSettingsVisibility)}
                                >
                                    {visOptions.map((opt) => (
                                        <MenuItem key={opt} value={opt}>
                                            {opt}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>Applies to new community posts. Overrideable per post.</FormHelperText>
                            </FormControl>

                            {/* Follower on Request */}
                            <Box>
                                <Tooltip title="When enabled, follow requests are auto-approved. Disable to require manual approval.">
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={data.followerOnRequest}
                                                onChange={(e) => handleChange("followerOnRequest", e.target.checked)}
                                                disabled={disabledAll}
                                            />
                                        }
                                        label="Auto-approve follower requests"
                                    />
                                </Tooltip>
                                <FormHelperText sx={{ ml: 7 }}>Turn off if you want to review followers first.</FormHelperText>
                            </Box>

                            {/* TODO: Implement Dark Mode */}
                            {/* <Box>
                            <Tooltip title="Toggle a darker theme for the app. This may update immediately across the UI.">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={data.darkMode}
                                            onChange={(e) => handleChange("darkMode", e.target.checked)}
                                            disabled={disabledAll}
                                        />
                                    }
                                    label="Dark mode"
                                />
                            </Tooltip>
                        </Box> */}

                            {/* Actions */}
                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="flex-end">
                                <Button variant="outlined" onClick={handleReset} disabled={disabledAll || !isDirty}>
                                    Reset
                                </Button>
                                <Button variant="contained" onClick={handleSave} disabled={disabledAll || !isDirty} sx={{ color: "white" }}>
                                    {saving ? "Saving…" : "Save changes"}
                                </Button>
                            </Stack>

                            {/* Error helper */}
                            {errorMsg && (
                                <Alert severity="error" onClose={() => setErrorMsg(null)}>
                                    {errorMsg}
                                </Alert>
                            )}
                        </Stack>
                    </CardContent>

                    <Snackbar
                        open={successOpen}
                        autoHideDuration={2000}
                        onClose={() => setSuccessOpen(false)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    >
                        <Alert onClose={() => setSuccessOpen(false)} severity="success" sx={{ width: "100%" }}>
                            Settings saved
                        </Alert>
                    </Snackbar>
                </Card>
            </Box>
        </ThemeProvider>
    );
};

export default Settings;
